import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Mail, Send, Sparkles } from 'lucide-react';
import { api, type SupportAskResult, type SupportAskTurn } from '../../lib/api';
import { recordAssistantQuestion } from '../../lib/supportContext';

// Capa 2 del soporte escalonado (GOL-11 / D8). Conoce el panel a través de los
// artículos de ayuda y nada más: no ve los datos del negocio, y cuando no sabe,
// la salida honesta es escalar a una persona — por eso el botón de soporte vive
// dentro de la conversación y no escondido al final.

type Turn = {
  role: 'user' | 'assistant';
  content: string;
  articles?: SupportAskResult['articles'];
  /** Marca las respuestas donde el asistente admitió no saber. */
  unresolved?: boolean;
};

type Props = {
  initialQuestion?: string;
  onBack: () => void;
  onContact: () => void;
  onOpenArticle: (id: string) => void;
  onNavigate: (to: string) => void;
};

export default function HelpAssistant({
  initialQuestion, onBack, onContact, onOpenArticle, onNavigate,
}: Props) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const askedRef = useRef(false);

  const ask = useCallback(async (question: string) => {
    const clean = question.trim();
    if (!clean || loading) return;
    setError(null);
    setInput('');
    const history: SupportAskTurn[] = turns.map((t) => ({ role: t.role, content: t.content }));
    setTurns((prev) => [...prev, { role: 'user', content: clean }]);
    recordAssistantQuestion(clean);
    setLoading(true);
    try {
      const result = await api.support.ask({ question: clean, history });
      setTurns((prev) => [...prev, {
        role: 'assistant',
        content: result.answer,
        articles: result.articles,
        unresolved: !result.resolved,
      }]);
    } catch (e) {
      setError((e as Error).message || 'Ahora mismo no puedo responderte.');
    } finally {
      setLoading(false);
    }
  }, [loading, turns]);

  // La pregunta que traía el buscador se manda sola: el cliente ya la escribió
  // una vez y volver a pedírsela sería cobrarle el mismo esfuerzo dos veces.
  useEffect(() => {
    if (initialQuestion && !askedRef.current) {
      askedRef.current = true;
      ask(initialQuestion);
    }
  }, [initialQuestion, ask]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns.length, loading]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex shrink-0 items-center gap-1.5 self-start text-sm text-ui-text-muted transition-colors hover:text-ui-text"
      >
        <ArrowLeft size={14} />
        Volver a preguntas frecuentes
      </button>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
        <div className="flex items-start gap-2 rounded-lg border border-ui-border bg-ui-surface px-3 py-2.5">
          <Sparkles size={15} className="mt-0.5 shrink-0 text-ui-accent" />
          <p className="text-sm text-ui-text-muted">
            Pregúntame lo que quieras sobre tu panel. Conozco cómo funciona Destakia;
            no veo la información de tu negocio.
          </p>
        </div>

        {turns.map((turn, i) => (
          <motion.div
            key={`${turn.role}-${i}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.16 }}
            className={turn.role === 'user' ? 'flex justify-end' : ''}
          >
            {turn.role === 'user' ? (
              <p className="max-w-[85%] rounded-xl rounded-br-sm bg-ui-accent/15 px-3 py-2 text-sm text-ui-text">
                {turn.content}
              </p>
            ) : (
              <div className="space-y-2 rounded-xl rounded-bl-sm border border-ui-border bg-ui-surface px-3 py-2.5">
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-ui-text-muted">{turn.content}</p>

                {turn.articles?.map((article) => (
                  <div key={article.id} className="flex flex-wrap items-center gap-2">
                    {article.to && (
                      <button
                        type="button"
                        onClick={() => onNavigate(article.to as string)}
                        className="inline-flex items-center gap-1.5 rounded-md bg-ui-accent px-2.5 py-1.5 text-sm font-medium text-ui-text-inverse transition-colors hover:bg-ui-accent-hover"
                      >
                        {article.toLabel ?? 'Ir ahí'}
                        <ArrowRight size={13} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onOpenArticle(article.id)}
                      className="text-sm text-ui-accent hover:underline"
                    >
                      {article.question}
                    </button>
                  </div>
                ))}

                {turn.unresolved && (
                  <button
                    type="button"
                    onClick={onContact}
                    className="inline-flex items-center gap-2 rounded-md border border-ui-border px-2.5 py-1.5 text-sm font-medium text-ui-text transition-colors hover:border-ui-accent"
                  >
                    <Mail size={14} />
                    Escribir a soporte
                  </button>
                )}
              </div>
            )}
          </motion.div>
        ))}

        {loading && (
          <div className="flex items-center gap-1.5 rounded-xl rounded-bl-sm border border-ui-border bg-ui-surface px-3 py-3">
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                className="h-1.5 w-1.5 rounded-full bg-ui-text-subtle"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1, repeat: Infinity, delay: i * 0.15 }}
              />
            ))}
          </div>
        )}

        {error && (
          <div className="space-y-2 rounded-lg border border-ui-warning/40 bg-ui-warning/10 px-3 py-2.5">
            <p className="text-sm text-ui-text">{error}</p>
            <button
              type="button"
              onClick={onContact}
              className="inline-flex items-center gap-2 rounded-md border border-ui-border px-2.5 py-1.5 text-sm font-medium text-ui-text transition-colors hover:border-ui-accent"
            >
              <Mail size={14} />
              Escribir a soporte
            </button>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); ask(input); }}
        className="flex shrink-0 items-end gap-2 border-t border-ui-border pt-3"
      >
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(input); }
          }}
          rows={2}
          placeholder="Escribe tu pregunta…"
          disabled={loading}
          className="min-w-0 flex-1 resize-none rounded-md border border-ui-border bg-ui-bg px-3 py-2 text-sm text-ui-text placeholder:text-ui-text-subtle focus:border-ui-accent focus:outline-none disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          aria-label="Enviar pregunta"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-ui-accent text-ui-text-inverse transition-colors hover:bg-ui-accent-hover disabled:opacity-50"
        >
          <Send size={15} />
        </button>
      </form>
    </div>
  );
}
